import React, { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical, Save, Sparkles } from "lucide-react";
import { updateCreatorUpsells, formatApiError } from "../lib/api";
import { toast } from "sonner";

const MAX_UPSELLS = 8;
const SUGGESTIONS = [
  { name: "Facecam", price: 5, description: "I'll include my facecam reaction in the video." },
  { name: "Voiceover / Commentary", price: 10, description: "Pro voiceover laid over the gameplay." },
  { name: "Custom Intro", price: 15, description: "Branded intro with your logo." },
  { name: "Rush Delivery (24h)", price: 20, description: "Priority queue, delivered within 24h." },
  { name: "Extended Video (5+ min)", price: 25, description: "Longer cut for in-depth coverage." },
  { name: "Thumbnail Design", price: 8, description: "Click-magnet custom thumbnail." },
  { name: "Raw Footage", price: 12, description: "Original capture files alongside the edit." },
  { name: "Vertical 9:16 Cut", price: 10, description: "Shorts/Reels-ready vertical version." },
  { name: "Background Music", price: 5, description: "Royalty-free music bed." },
];

const newRow = () => ({ id: `tmp-${Math.random().toString(36).slice(2, 9)}`, name: "", description: "", price: 5, enabled: true });

export default function UpsellsManager({ creatorId, initial }) {
  const [items, setItems] = useState(() =>
    Array.isArray(initial) && initial.length ? initial.map((u) => ({ ...u, id: u.id || `tmp-${Math.random().toString(36).slice(2, 9)}` })) : []
  );
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (Array.isArray(initial)) {
      setItems(initial.map((u) => ({ ...u, id: u.id || `tmp-${Math.random().toString(36).slice(2, 9)}` })));
    }
  }, [creatorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, patch) => setItems((arr) => arr.map((it, k) => (k === i ? { ...it, ...patch } : it)));
  const remove = (i) => {
    if (!window.confirm("Remove this add-on?")) return;
    setItems((arr) => arr.filter((_, k) => k !== i));
  };
  const add = (preset) => {
    setItems((arr) => {
      if (arr.length >= MAX_UPSELLS) return arr;
      const r = preset ? { ...newRow(), name: preset.name, description: preset.description, price: preset.price } : newRow();
      return [...arr, r];
    });
  };

  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIndex === null || dragIndex === i) return;
    setItems((arr) => {
      const copy = [...arr];
      const [moved] = copy.splice(dragIndex, 1);
      copy.splice(i, 0, moved);
      return copy;
    });
    setDragIndex(null);
  };

  const save = async () => {
    for (const it of items) {
      if (!it.name.trim()) {
        toast.error("Each add-on needs a name.");
        return;
      }
      if (it.price < 0 || it.price > 10000) {
        toast.error(`Price for "${it.name}" is invalid.`);
        return;
      }
    }
    setSaving(true);
    try {
      const r = await updateCreatorUpsells(
        creatorId,
        items.map(({ id, name, description, price, enabled }) => ({
          id: id?.startsWith("tmp-") ? null : id,
          name, description, price: Number(price), enabled,
        }))
      );
      setItems(r.upsells);
      toast.success("Add-ons saved");
    } catch (err) {
      toast.error(formatApiError(err, "Could not save add-ons."));
    } finally {
      setSaving(false);
    }
  };

  const remaining = MAX_UPSELLS - items.length;

  return (
    <section data-testid="upsells-manager" className="cc-surface rounded-2xl p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="cc-overline inline-flex items-center gap-1.5">
            <Sparkles size={11} className="text-[#00ff88]" /> My add-ons
          </div>
          <p className="text-xs text-[#a0a0b0] mt-1.5 max-w-md">
            Optional extras companies can purchase on top of your base price. Up to {MAX_UPSELLS}.
          </p>
        </div>
        <button
          type="button"
          data-testid="upsell-save-btn"
          onClick={save}
          disabled={saving}
          className="cc-btn-primary rounded-lg px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={12} /> {saving ? "Saving…" : "Save add-ons"}
        </button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div data-testid="upsells-empty" className="text-xs text-[#606070] italic py-3">
            No add-ons yet. Pick a suggestion below or add a custom one.
          </div>
        )}
        {items.map((it, i) => (
          <div
            key={it.id}
            data-testid={`upsell-row-${i}`}
            draggable
            onDragStart={() => onDragStart(i)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(i)}
            className={`grid grid-cols-12 gap-2 items-center p-3 rounded-lg border ${
              it.enabled ? "border-[#15152a] bg-[#06060c]" : "border-[#15152a]/50 bg-[#06060c]/40 opacity-70"
            } hover:border-[#1f1f3a] transition-colors`}
          >
            <div className="col-span-1 flex items-center justify-center text-[#606070] cursor-grab active:cursor-grabbing">
              <GripVertical size={14} />
            </div>
            <input
              data-testid={`upsell-name-${i}`}
              value={it.name}
              maxLength={50}
              onChange={(e) => update(i, { name: e.target.value })}
              placeholder="Name (e.g. Facecam)"
              className="col-span-4 bg-transparent border-b border-transparent focus:border-[#00ff88]/40 outline-none text-sm text-white py-1 cc-focus-ring"
            />
            <input
              data-testid={`upsell-desc-${i}`}
              value={it.description || ""}
              maxLength={150}
              onChange={(e) => update(i, { description: e.target.value })}
              placeholder="Description (optional)"
              className="col-span-4 bg-transparent border-b border-transparent focus:border-[#00ff88]/40 outline-none text-xs text-[#a0a0b0] py-1 cc-focus-ring"
            />
            <div className="col-span-2 flex items-center gap-1">
              <span className="text-[#00ff88] font-mono text-xs">$</span>
              <input
                data-testid={`upsell-price-${i}`}
                type="number"
                min="0"
                value={it.price}
                onChange={(e) => update(i, { price: Number(e.target.value) || 0 })}
                className="w-full bg-transparent border-b border-transparent focus:border-[#00ff88]/40 outline-none text-sm text-white font-mono py-1 cc-focus-ring"
              />
            </div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              <button
                type="button"
                data-testid={`upsell-toggle-${i}`}
                role="switch"
                aria-checked={it.enabled}
                onClick={() => update(i, { enabled: !it.enabled })}
                className={`relative w-8 h-4 rounded-full p-0.5 transition-colors ${it.enabled ? "bg-[#00ff88]/40" : "bg-[#15152a]"}`}
                title={it.enabled ? "Enabled" : "Disabled"}
              >
                <span className={`block w-3 h-3 rounded-full transition-transform ${it.enabled ? "translate-x-4 bg-[#00ff88]" : "bg-[#606070]"}`} />
              </button>
              <button
                type="button"
                data-testid={`upsell-delete-${i}`}
                onClick={() => remove(i)}
                className="text-[#606070] hover:text-[#ff4444] p-1"
                aria-label="Delete add-on"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {remaining > 0 && (
        <div className="pt-1">
          <button
            type="button"
            data-testid="upsell-add-blank"
            onClick={() => add()}
            className="text-xs text-[#00ff88] hover:underline inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add a custom add-on
          </button>
          <span className="text-[11px] text-[#606070] ml-3">{remaining} slot{remaining > 1 ? "s" : ""} left</span>
        </div>
      )}

      {remaining > 0 && (
        <div data-testid="upsell-suggestions" className="pt-3 border-t border-[#15152a]">
          <div className="cc-overline mb-2">Quick suggestions</div>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.filter((s) => !items.some((it) => it.name.toLowerCase() === s.name.toLowerCase())).slice(0, 8).map((s) => (
              <button
                key={s.name}
                type="button"
                data-testid={`upsell-suggest-${s.name}`}
                onClick={() => add(s)}
                className="px-2.5 py-1 rounded-md text-[11px] border border-[#15152a] bg-[#06060c] text-[#a0a0b0] hover:text-white hover:border-[#00ff88]/30 transition"
              >
                {s.name} <span className="text-[#00ff88] font-mono ml-1">+${s.price}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
